import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useAvailability, useUpdateAvailability } from '@/hooks/useAvailability'
import { useProviderProfile, useUpdateProviderProfile } from '@/hooks/useProviderProfile'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { TimezoneSelect } from '@/components/availability/TimezoneSelect'
import { WeeklyScheduleEditor } from '@/components/availability/WeeklyScheduleEditor'
import { DateOverrideCalendar } from '@/components/availability/DateOverrideCalendar'
import { EffectiveSchedulePreview } from '@/components/availability/EffectiveSchedulePreview'
import { detectBrowserTimezone, hasWeeklyBlockErrors } from '@/components/availability/utils'
import LoadingPage from '@/components/shared/LoadingPage'
import ErrorState from '@/components/shared/ErrorState'
import type { Availability } from '@/types/availability'

export default function AvailabilityPage() {
  const {
    data: availability,
    isLoading: loadingAvail,
    error: availError,
    refetch: refetchAvail,
  } = useAvailability()
  const {
    data: profile,
    isLoading: loadingProfile,
    error: profileError,
    refetch: refetchProfile,
  } = useProviderProfile()
  const updateAvailability = useUpdateAvailability()
  const updateProfile = useUpdateProviderProfile()

  const [draft, setDraft] = useState<Availability | null>(null)
  const [tzDraft, setTzDraft] = useState<string | null>(null)

  useEffect(() => {
    if (availability && !draft) {
      setDraft(availability)
    }
  }, [availability, draft])

  if (loadingAvail || loadingProfile) {
    return <LoadingPage variant="form" />
  }

  if (availError || !availability) {
    return <ErrorState message="Failed to load availability." onRetry={() => refetchAvail()} />
  }

  if (profileError || !profile) {
    return <ErrorState message="Failed to load provider profile." onRetry={() => refetchProfile()} />
  }

  const effectiveDraft: Availability = draft ?? availability
  // Show the provider's saved tz, or the user's draft choice, or a browser-
  // detected fallback. The fallback is only persisted when the user saves.
  const timezone = tzDraft ?? profile.timezone ?? detectBrowserTimezone()
  const availabilityDirty =
    JSON.stringify(effectiveDraft.recurring_blocks) !==
      JSON.stringify(availability.recurring_blocks) ||
    JSON.stringify(effectiveDraft.date_overrides) !==
      JSON.stringify(availability.date_overrides)
  const tzDirty = tzDraft !== null && tzDraft !== profile.timezone
  const needsTzBackfill = !profile.timezone && tzDraft === null
  const isDirty = availabilityDirty || tzDirty || needsTzBackfill
  const blocksHaveErrors = hasWeeklyBlockErrors(effectiveDraft.recurring_blocks)

  function handleTimezoneChange(tz: string) {
    setTzDraft(tz)
  }

  function handleDiscard() {
    setDraft(availability ?? null)
    setTzDraft(null)
  }

  async function handleSave() {
    const tasks: Promise<unknown>[] = []
    if (availabilityDirty && draft) {
      tasks.push(updateAvailability.mutateAsync(draft))
    }
    if (profile && (tzDirty || needsTzBackfill)) {
      const nextTz = tzDraft ?? detectBrowserTimezone()
      tasks.push(updateProfile.mutateAsync({ ...profile, timezone: nextTz }))
    }
    if (tasks.length === 0) return
    try {
      await Promise.all(tasks)
      setTzDraft(null)
      toast.success('Availability saved')
    } catch {
      toast.error('Failed to save availability')
    }
  }

  const isSaving = updateAvailability.isPending || updateProfile.isPending

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Availability</h1>
        <div className="flex items-center gap-2">
          {isDirty && (
            <Button
              type="button"
              variant="outline"
              onClick={handleDiscard}
              disabled={isSaving}
            >
              Discard
            </Button>
          )}
          <Button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || blocksHaveErrors || isSaving}
          >
            {isSaving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Timezone</CardTitle>
        </CardHeader>
        <CardContent>
          <TimezoneSelect value={timezone} onChange={handleTimezoneChange} />
          {needsTzBackfill && (
            <p className="mt-2 text-xs text-amber-700">
              Using your browser timezone ({timezone}). Save to confirm.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recurring Weekly Schedule</CardTitle>
        </CardHeader>
        <CardContent>
          <WeeklyScheduleEditor
            blocks={effectiveDraft.recurring_blocks}
            onChange={(recurring_blocks) =>
              setDraft({ ...effectiveDraft, recurring_blocks })
            }
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Date Overrides</CardTitle>
        </CardHeader>
        <CardContent>
          <DateOverrideCalendar
            overrides={effectiveDraft.date_overrides}
            onChange={(date_overrides) => setDraft({ ...effectiveDraft, date_overrides })}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Effective Schedule Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <EffectiveSchedulePreview availability={effectiveDraft} timezone={timezone} />
        </CardContent>
      </Card>
    </div>
  )
}
